import {
  Deploy,
  NftTokenEntryPoint,
  TransactorHashType,
} from '@block-explorer/entities';
import { MessageData } from './types';
import {
  getNftTokenIdsFromArguments,
  getNftTokensQuantityFromArgs,
  guardedDeriveSplitDataFromArguments,
} from './utils';

const getNftTransactorsDataFromArgs = (args) => {
  const tokenOwner = guardedDeriveSplitDataFromArguments(
    args.token_owner,
    'Account'
  );
  const owner = guardedDeriveSplitDataFromArguments(args.owner, 'Account');
  const from = guardedDeriveSplitDataFromArguments(args.from, 'Account');

  const sourceKey = guardedDeriveSplitDataFromArguments(
    args.source_key,
    'Account'
  );

  const targetKey = guardedDeriveSplitDataFromArguments(
    args.target_key,
    'Account'
  );
  const recipientHash = guardedDeriveSplitDataFromArguments(
    args.recipient,
    'Account'
  );

  const accountSpenderHash = guardedDeriveSplitDataFromArguments(
    args.spender,
    'Account'
  );

  const contractSpenderHash = guardedDeriveSplitDataFromArguments(
    args.spender,
    'Hash'
  );
  const operatorHash = guardedDeriveSplitDataFromArguments(
    args.operator,
    'Hash'
  );
  const to = guardedDeriveSplitDataFromArguments(args.to, 'Account');

  return {
    tokenOwner,
    owner,
    sourceKey,
    targetKey,
    recipientHash,
    contractSpenderHash,
    operatorHash,
    accountSpenderHash,
    from,
    to,
  };
};

const prepareActionMessageData = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any,
  t
): MessageData | null => {
  const { entryPoint, args } = deploy;

  const entryPointName = entryPoint?.name || '';
  const actionName: Record<NftTokenEntryPoint, string> = {
    [NftTokenEntryPoint.approve]: t('Approve transfer'),
    [NftTokenEntryPoint.burn]: t('Burn'),
    [NftTokenEntryPoint.mint]: t('Mint'),
    [NftTokenEntryPoint.transfer]: t('Transfer'),
    [NftTokenEntryPoint.transfer_from]: t('Transfer'),
    [NftTokenEntryPoint.update_token_meta]: t('Update metadata'),
    [NftTokenEntryPoint.set_approval_for_all]: t('Approve transfer all'),
  };

  if (actionName[entryPointName] === undefined) {
    return null;
  }

  const numOfTokens =
    entryPointName !== NftTokenEntryPoint.approve &&
    entryPointName !== NftTokenEntryPoint.update_token_meta
      ? getNftTokensQuantityFromArgs(args)
      : null;

  const {
    recipientHash,
    tokenOwner,
    contractSpenderHash,
    operatorHash,
    accountSpenderHash,
    to,
  } = getNftTransactorsDataFromArgs(args);

  const accountHash =
    recipientHash?.hash ||
    tokenOwner?.hash ||
    accountSpenderHash?.hash ||
    to?.hash;

  const actionOutcome: Partial<MessageData> = {
    ...(numOfTokens && { numOfTokens: numOfTokens }),
    ...(accountHash && {
      prefix1: t('to'),
      account1: {
        accountHash: accountHash,
        publicKey: accountHash && getPublicKeyByAccountHash(accountHash),
      },
    }),
    ...((contractSpenderHash?.hash || operatorHash?.hash) && {
      prefix1: t('to'),
      account1: {
        hash: contractSpenderHash?.hash || operatorHash?.hash,
      },
    }),
  };

  return {
    entryPointName: entryPointName,
    actionName: actionName[entryPointName] || entryPointName,
    ...actionOutcome,
  };
};

const prepareActionMessageDataForDeployDetails = (
  deploy: Deploy,
  getPublicKeyByAccountHash: (hash: string) => any,
  t
): MessageData | null => {
  const { entryPoint, args } = deploy;
  const entryPointName = entryPoint?.name || '';

  const actionName = {
    [NftTokenEntryPoint.transfer]: t('Transfer'),
    [NftTokenEntryPoint.transfer_from]: t('Transfer'),
    [NftTokenEntryPoint.approve]: t('Approve transfer rights'),
    [NftTokenEntryPoint.burn]: t('Burn'),
    [NftTokenEntryPoint.mint]: t('Mint'),
    [NftTokenEntryPoint.update_token_meta]: t('Update metadata'),
    [NftTokenEntryPoint.set_approval_for_all]: t('Approve transfer rights'),
  };

  const contractPrefixesMap = {
    [NftTokenEntryPoint.approve]: t('for'),
    [NftTokenEntryPoint.update_token_meta]: t('for'),
    [NftTokenEntryPoint.mint]: t('of'),
    [NftTokenEntryPoint.set_approval_for_all]: t('of'),
  };

  const senderPrefixesMap = {
    [NftTokenEntryPoint.transfer]: t('from'),
    [NftTokenEntryPoint.transfer_from]: t('from'),
    [NftTokenEntryPoint.burn]: t('owned by'),
    [NftTokenEntryPoint.approve]: t('to'),
    [NftTokenEntryPoint.mint]: t('to'),
  };

  if (actionName[entryPointName] === undefined) {
    return null;
  }

  const nftTokenIds = getNftTokenIdsFromArguments(args);
  const {
    recipientHash,
    tokenOwner,
    contractSpenderHash,
    operatorHash,
    sourceKey,
    owner,
    targetKey,
    accountSpenderHash,
    from,
    to,
  } = getNftTransactorsDataFromArgs(args);

  const account1Hash =
    tokenOwner?.hash ||
    sourceKey?.hash ||
    owner?.hash ||
    accountSpenderHash?.hash ||
    from?.hash;
  const account2Hash = recipientHash?.hash || targetKey?.hash || to?.hash;

  const contractPrefix = contractPrefixesMap[entryPointName];
  const senderPrefix = senderPrefixesMap[entryPointName];

  const actionOutcome: Partial<MessageData> = {
    nftTokenIds: nftTokenIds,
    contractPrefix,
    ...(account1Hash && {
      prefix1: senderPrefix,
      account1: {
        hash: account1Hash,
        publicKey: account1Hash && getPublicKeyByAccountHash(account1Hash),
        hashType: TransactorHashType.account,
      },
    }),
    ...(contractSpenderHash?.hash && {
      prefix1: senderPrefix,
      account1: {
        hash: contractSpenderHash?.hash,
        hashType: TransactorHashType.hash,
      },
    }),
    ...(account2Hash && {
      account2: {
        hash: account2Hash,
        publicKey: account2Hash && getPublicKeyByAccountHash(account2Hash),
        hashType: TransactorHashType.account,
      },
    }),
    ...(operatorHash?.hash && {
      account2: {
        hash: operatorHash?.hash,
        hashType: TransactorHashType.hash,
      },
    }),
  };

  return {
    entryPointName: entryPointName,
    actionName: actionName[entryPointName] || entryPointName,
    ...actionOutcome,
  };
};

export const nftTokenMessage = () => {
  const getRelevantAccountHashesFromArgs = () => {
    return [
      'token_owner',
      'recipient',
      'spender',
      'operator',
      'target_key',
      'source_key',
      'from',
      'to',
    ];
  };

  return {
    prepareActionMessageData,
    prepareActionMessageDataForDeployDetails,
    getRelevantAccountHashesFromArgs,
  };
};
